import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, FileText, Smartphone, Search, Eye, Layout } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Link, useNavigate } from 'react-router-dom';
import { db, collection, onSnapshot, query, orderBy, doc, getDoc, addDoc, Timestamp } from '@/firebase';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import PaymentModal from './PaymentModal';

export default function Products({ limit, hideBanner, hideFilters }: { limit?: number, hideBanner?: boolean, hideFilters?: boolean }) {
  const navigate = useNavigate();
  const { user, profile, openAuthModal } = useAuth();
  const [filter, setFilter] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [products, setProducts] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [promo, setPromo] = React.useState<any>(null);
  const [isBuying, setIsBuying] = React.useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = React.useState(false);
  const [currentOrder, setCurrentOrder] = React.useState<{id: string, productName: string, amount: number} | null>(null);

  React.useEffect(() => {
    if (!hideBanner) {
      const fetchPromo = async () => {
        try {
          const promoSnap = await getDoc(doc(db, 'settings', 'promo_products'));
          if (promoSnap.exists()) {
            setPromo(promoSnap.data());
          } else {
            // Backward compatibility
            const oldPromoSnap = await getDoc(doc(db, 'settings', 'promo'));
            if (oldPromoSnap.exists()) {
              setPromo(oldPromoSnap.data());
            } else {
              setPromo({
                isActive: true,
                title: 'Akselerasikan Proyek Anda dengan Sistem Siap Pakai',
                subtitle: 'Template dokumen praktis dan sistem aplikasi siap pakai untuk kemudahan administrasi proyek konstruksi Anda.',
                bannerUrl: 'https://images.unsplash.com/photo-1541888086225-f673f41cde00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                badgeText: 'Produk Unggulan'
              });
            }
          }
        } catch (error) {
          console.error('Error fetching promo:', error);
        }
      };
      fetchPromo();
    }

    const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const pData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(pData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching products:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [hideBanner]);

  const filteredProducts = products.filter(p => {
    const matchesFilter = filter === 'all' || p.category === filter;
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const displayedProducts = limit ? filteredProducts.slice(0, limit) : filteredProducts;

  const handleQuickBuy = async (product: any) => {
    if (!user) {
      toast.info("Silakan masuk atau daftarkan akun baru Anda terlebih dahulu untuk melanjutkan pembelian.");
      openAuthModal('register', `Daftar akun atau masuk untuk membeli "${product.name}". File dokumen/aplikasi dan faktur resmi Anda akan tersimpan aman di akun Anda.`);
      return;
    }

    setIsBuying(product.id);
    try {
      const orderData = {
        userId: user.uid,
        userEmail: user.email,
        userName: profile?.displayName || user.displayName || 'User',
        userPhone: profile?.phoneNumber || '',
        productId: product.id,
        productName: product.name,
        amount: product.price,
        status: 'pending',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, 'orders'), orderData);
      
      setCurrentOrder({
        id: docRef.id,
        productName: product.name,
        amount: product.price
      });
      
      toast.success("Pesanan berhasil dibuat!");
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Quick buy error:', error);
      toast.error("Gagal membuat pesanan. Silakan coba lagi.");
    } finally {
      setIsBuying(null);
    }
  };

  return (
    <section className="py-12 md:py-24 bg-background transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 md:px-0">
        
        {/* Promotional Banner */}
        {!hideBanner && promo?.isActive && (
          <div className="mb-10 md:mb-16 bg-card sm:rounded-2xl md:rounded-3xl overflow-hidden relative shadow-xl -mx-4 sm:mx-0 border border-border">
            {promo.bannerUrl && (
              <img 
                src={promo.bannerUrl} 
                alt="Promo Banner" 
                className="w-full h-auto block" 
                referrerPolicy="no-referrer" 
              />
            )}
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center mb-12 gap-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
                Produk <span className="text-primary">Digital</span> {limit ? 'Unggulan' : 'Kami'}
              </h2>
              <p className="text-foreground/60">
                Pilih dokumen atau aplikasi yang Anda butuhkan untuk mempercepat proses administrasi usaha Anda.
              </p>
            </div>
          
          {!hideFilters && (
            <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                <Input 
                   placeholder="Cari produk..." 
                   className="pl-10 border-foreground/10 focus:border-primary rounded-xl bg-card text-foreground"
                   value={search}
                   onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex bg-muted p-1 rounded-xl">
                {['all', 'document', 'application'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      filter === f ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground/60 hover:text-foreground'
                    }`}
                  >
                    {f === 'all' ? 'Semua' : f === 'document' ? 'Dokumen' : 'Aplikasi'}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {displayedProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedProducts.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <Card className="overflow-hidden border border-foreground/5 shadow-lg shadow-foreground/5 hover:shadow-2xl hover:shadow-foreground/10 transition-all group h-full flex flex-col bg-card">
                  <div className="relative aspect-video overflow-hidden">
                    <img 
                      src={product.image || 'https://picsum.photos/seed/placeholder/400/300'} 
                      alt={product.name} 
                      className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-500"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                      {product.name}
                    </CardTitle>
                    <div className="mt-2 flex flex-col gap-1.5">
                       {product.type && (
                        <div className="bg-muted text-blue-600 border border-blue-100 font-medium py-1 px-3 rounded-full text-[10px] sm:text-[11px] leading-tight w-fit">
                          {product.type}
                        </div>
                      )}
                      <Badge className={`${product.category === 'document' ? 'bg-blue-50' : 'bg-purple-50'} ${product.category === 'document' ? 'text-blue-600' : 'text-purple-600'} border-none shadow-none hover:bg-transparent px-0 text-[11px] h-fit py-0`}>
                        {product.category === 'document' ? <FileText className="h-3 w-3 mr-1" /> : <Smartphone className="h-3 w-3 mr-1" />}
                        {product.category === 'document' ? 'Dokumen' : 'Aplikasi'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <p className="text-foreground/60 text-sm line-clamp-3 mb-4 leading-relaxed text-left font-body">
                      {product.desc}
                    </p>
                    <div className="text-2xl font-bold text-foreground">
                      Rp {product?.price?.toLocaleString('id-ID') || 0}
                    </div>
                  </CardContent>
                  <CardFooter className="pt-0 flex flex-col gap-2">
                    <div className="grid grid-cols-2 gap-2 w-full">
                      <Button asChild variant="outline" className="border-foreground/10 text-foreground hover:bg-foreground/5 font-bold h-11 rounded-xl transition-all text-xs">
                        <Link to={`/product/${product.id}`}>
                          <Eye className="mr-1.5 h-3.5 w-3.5" /> Detail
                        </Link>
                      </Button>
                      {product.appLink ? (
                        <Button asChild variant="secondary" className="bg-gold/10 text-gold-foreground border border-gold/20 hover:bg-gold/20 font-bold h-11 rounded-xl transition-all text-xs shadow-none">
                          <a href={product.appLink} target="_blank" rel="noopener noreferrer">
                            <Layout className="mr-1.5 h-3.5 w-3.5" /> Buka App
                          </a>
                        </Button>
                      ) : (
                        <div className="h-11 rounded-xl bg-muted/50 border border-muted flex items-center justify-center pointer-events-none">
                           <span className="text-[10px] text-foreground/30 font-bold">READY TO BUY</span>
                        </div>
                      )}
                    </div>
                    <Button 
                      onClick={() => handleQuickBuy(product)}
                      disabled={isBuying === product.id}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-11 rounded-xl shadow-lg shadow-primary/20"
                    >
                      {isBuying === product.id ? (
                        "Memproses..."
                      ) : (
                        <>
                          <ShoppingCart className="mr-2 h-4 w-4" /> Beli Sekarang
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-card rounded-3xl border border-border shadow-sm">
            <ShoppingCart className="mx-auto h-16 w-16 text-foreground/20 mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">Belum ada produk</h3>
            <p className="text-foreground/60 max-w-md mx-auto">
              Saat ini belum ada produk yang tersedia. Admin akan segera menambahkan produk baru.
            </p>
          </div>
        )}

        {limit && displayedProducts.length < filteredProducts.length && (
          <div className="mt-12 text-center">
            <Button asChild className="bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold h-12 px-8 rounded-xl shadow-none">
              <Link to="/products">Lihat Semua Produk</Link>
            </Button>
          </div>
        )}

      </div>
      
      {/* SEO Keyword tags for indexing */}
      <div className="sr-only">
        Dokumen Penawaran file Excel, Dokumen SMKK File Excel, PMPM File Excel, RKPPL File Excel, 
        RMPK File Excel, RKK File Excel, RMLLP File Excel, Program mutu File Excel, 
        Laporan Penerapan RKK File Excel, RKK Pelaksanaan File Excel, RKK Penawaran File Excel, 
        Rncangan Konseptual SMKK File Excel, RKK Konsultan File Excel, Formulir Isian Kualifikasi File Excel, 
        Kumpulan Dokumen Kontraktor File Excel, Aplikasi Kontraktor
      </div>

      <PaymentModal 
        isOpen={showPaymentModal} 
        onClose={() => {
          setShowPaymentModal(false);
          navigate('/dashboard');
        }} 
        order={currentOrder} 
      />
    </div>
  </section>
);
}
